import React, { useEffect, useState } from "react";
import { fixedIncomeApi } from "../apiService";
import { VehicleBookings } from "../types";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Tab,
  Tabs,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  DirectionsBus as BusIcon,
  CalendarMonth as CalendarIcon,
  Visibility,
} from "@mui/icons-material";
import { formatCurrency, formatDate } from "../../../utils/utils";
import { Loading } from "../../../components/Loading";
import { tableCellStyles, tableHeaderStyles } from "../../../utils/styles";

const MarketLoadJourneys = ({
  handleEditBooking,
  handleDailyLogs,
  handleNewBooking,
  searchFilters,
}: {
  handleEditBooking: (id: string) => void;
  handleDailyLogs: (
    id: string,
    vehicleId: string,
    vehicleNumber: string
  ) => void;
  handleNewBooking: () => void;
  searchFilters: any;
}) => {
  const [contracts, setContracts] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const fetchContractsWithFilters = async () => {
    setIsLoading(true);
    try {
      // Only pass non-empty filter values
      const activeFilters = {
        ...(searchFilters.vehicleNumber && {
          vehicleNumber: searchFilters.vehicleNumber,
        }),
        ...(searchFilters.startDate && { startDate: searchFilters.startDate }),
        ...(searchFilters.routeFrom && { routeFrom: searchFilters.routeFrom }),
        ...(searchFilters.routeTo && { routeTo: searchFilters.routeTo }),
      };

      const response = await fixedIncomeApi.getAllContractDetails(
        "market-load",
        Object.keys(activeFilters).length > 0 ? activeFilters : undefined
      );
      setContracts(response);
    } catch (error) {
      console.error("Error fetching contracts:", error);
      setContracts(null);
    } finally {
      setIsLoading(false);
      setIsFirstLoad(false);
    }
  };

  useEffect(() => {
    if (isFirstLoad) {
      fetchContractsWithFilters();
    }
  }, [isFirstLoad]);

  // Trigger API call when filters change
  useEffect(() => {
    if (isFirstLoad) return;
    const debounceTimeout = setTimeout(() => {
      fetchContractsWithFilters();
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [searchFilters]);

  const tableHeaders = [
    "Client Name",
    "Contract Dates",
    "Contract Amount",
    "Contract Expenses",
    "Actions",
  ];

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            height: "100vh",
          }}
        >
          <CircularProgress
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          />
        </Box>
      ) : (
        <>
          {contracts?.length > 0 ? (
            <>
              {contracts?.map((vehicleGroup: any, index: any) => (
                <Accordion
                  key={vehicleGroup?.vehicleNumber}
                  defaultExpanded={index === 0}
                  elevation={2}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`vehicle-${vehicleGroup?.vehicleNumber}-content`}
                    id={`vehicle-${vehicleGroup?.vehicleNumber}-header`}
                    sx={{
                      backgroundColor: "grey.50",
                      "&:hover": {
                        backgroundColor: "grey.100",
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <BusIcon sx={{ color: "primary.main", fontSize: 28 }} />
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            color: "text.primary",
                            lineHeight: 1.3,
                          }}
                        >
                          {vehicleGroup?.vehicleNumber}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            fontWeight: 500,
                          }}
                        >
                          {vehicleGroup.journeys.length} rental
                          {vehicleGroup.journeys.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "grey.25" }}>
                            {tableHeaders.map((header, index) => (
                              <TableCell
                                key={index}
                                sx={{
                                  ...tableHeaderStyles,
                                  fontSize: "0.85rem",
                                }}
                              >
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {vehicleGroup.journeys.map((booking: any) => (
                            <TableRow
                              key={booking?.id}
                              hover
                              sx={{
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                              }}
                            >
                              <TableCell sx={{ py: 1, ...tableCellStyles }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.primary",
                                  }}
                                >
                                  {booking?.clientData?.clientName}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1, ...tableCellStyles }}>
                                <Box>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: "text.primary",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    Start:{" "}
                                    {formatDate(booking?.journeyStartDateTime)}
                                  </Typography>
                                  {booking?.journeyEndDateTime && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: "0.75rem",
                                        lineHeight: 1.4,
                                        mt: 0.5,
                                      }}
                                    >
                                      End:{" "}
                                      {formatDate(booking?.journeyEndDateTime)}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: 1, ...tableCellStyles }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.primary",
                                  }}
                                >
                                  {formatCurrency(booking?.totalIncome)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1, ...tableCellStyles }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.primary",
                                  }}
                                >
                                  {formatCurrency(booking?.totalExpense)}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align="left"
                                sx={{ py: 1, ...tableCellStyles }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  justifyContent="left"
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const vehicleId =
                                        booking?.marketLoadData?.id;
                                      const vehicleNumber =
                                        booking?.marketLoadData?.vehicleNumber;
                                      if (vehicleId && vehicleNumber) {
                                        handleDailyLogs(
                                          booking.id,
                                          vehicleId,
                                          vehicleNumber
                                        );
                                      }
                                    }}
                                    title="View daily logs"
                                    sx={{
                                      color: "primary.main",
                                      "&:hover": {
                                        backgroundColor: "primary.lighter",
                                      },
                                    }}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          ) : (
            <>
              <Paper
                elevation={1}
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <BusIcon sx={{ fontSize: 60, color: "text.disabled", mb: 3 }} />
                <Typography
                  variant="h5"
                  sx={{
                    color: "text.secondary",
                    mb: 2,
                    fontWeight: 500,
                  }}
                >
                  No rentals found
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    mb: 4,
                    fontSize: "1rem",
                    lineHeight: 1.6,
                    maxWidth: 400,
                    mx: "auto",
                  }}
                >
                  Try adjusting your search filters or create a new fixed rental
                  to get started
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleNewBooking}
                  sx={{ px: 4, py: 1.5, fontSize: "0.95rem" }}
                >
                  Create New Rental
                </Button>
              </Paper>
            </>
          )}
        </>
      )}
    </>
  );
};

export default MarketLoadJourneys;
